import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { CustomDropdown, FilterOptionType, FilterValueType } from 'src/components/RealityCheckForm/CustomDropdown';
import { SdkStaff, StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { useAppStateContext } from 'src/AppState/AppState';
import { PageContainer } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs.style';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import confirmation from 'src/utils/confirm.jsx';
import addError from 'src/utils/error.jsx';
import { DateTime } from 'src_common/utils/time/time';

export const PLAY_BREAK_OPTIONS = {
    1: '24 hours',
    7: '1 week',
    21: '3 week',
    28: '4 week',
    42: '6 week',
};

export interface PlayBreakOptionsType {
    playBreakDuration: number | null;
    playBreakExpiry: string | null;
    playBreakRequestDate: string | null;
}
class PlayBreakState {
    @observable public selectedValue: string | null = null;
    @observable public expirityDate: string | null = null;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly customerId: number,
        private readonly permissionAccountsPlayBreak: boolean,
        private readonly models: ModelsState
    ) {
        makeAutoObservable(this);
    }

    @computed private get basicDataUserModel(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get playBreakOptions(): PlayBreakOptionsType {
        const userData = this.basicDataUserModel;

        return {
            playBreakDuration: userData?.playBreakDuration ?? null,
            playBreakExpiry: userData?.playBreakExpiry ?? null,
            playBreakRequestDate: userData?.playBreakRequestDate ?? null,
        };
    }

    @action public onUpdate = (changedValue: FilterValueType): void => {
        if (typeof changedValue === 'string') {
            this.selectedValue = changedValue;
            this.expirityDate = DateTime.current().addDays(parseInt(changedValue)).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        }
    };

    public PLAY_BREAK_OPTIONS_ARRAY = (): Array<FilterOptionType<string>> => {
        const out: Array<FilterOptionType<string>> = [];

        for (const [key, value] of Object.entries(PLAY_BREAK_OPTIONS)) {
            out.push({
                value: key,
                label: value,
            });
        }

        return out;
    };

    @action public onSubmit = async (): Promise<void> => {
        if (this.permissionAccountsPlayBreak === true || this.selectedValue === null) {
            return;
        }
        const selectedValueNumber = parseInt(this.selectedValue);

        const selectedExpiredDate = DateTime.current().addDays(selectedValueNumber).unixMs();
        const ongoingExpiredDate = DateTime.from(this.playBreakOptions.playBreakExpiry ?? '')?.unixMs();

        if (selectedExpiredDate < (ongoingExpiredDate ?? 0)) {
            addError("Playbreak can't be requested for a shorter duration than the current one.");
            return;
        }

        const result = await confirmation(
            `Are you sure that you want to set play break for ${this.selectedValue} days`,
            'ok'
        );

        //@ts-expect-error
        if (result !== 'ok') {
            return;
        }

        await this.onSetPlayBreak(selectedValueNumber);
    };

    @action private onSetPlayBreak = async (selectedValue: number): Promise<void> => {
        try {
            await this.sdkStaff.setPlayBreak({
                customer_id: this.customerId,
                requestBody: {
                    days: selectedValue,
                },
            });
        } catch (e) {
            console.error(e);
        }
    };
}

interface PlayBreakTabPropsType {
    customerId: number;
    permissionAccountsPlayBreak: boolean;
}

export const PlayBreakTab = observer(
    'PlayBreakTab',
    ({ customerId, permissionAccountsPlayBreak }: PlayBreakTabPropsType) => {
        const { sdkStaff, models } = useAppStateContext();
        const [state] = React.useState(
            () => new PlayBreakState(sdkStaff, customerId, permissionAccountsPlayBreak, models)
        );

        return (
            <PageContainer className='col-4'>
                <CustomDropdown
                    optionsList={state.PLAY_BREAK_OPTIONS_ARRAY}
                    selectedValue={(): string | null => state.selectedValue}
                    onChangeCallback={state.onUpdate}
                    placeholder='Play break period'
                    dataTest='play-break-period'
                    labelHeader='Play break period'
                />
                <div
                    className='play-break-expiry'
                    data-test='play-break-expiry-date'
                >
                    Expiry date:{' '}
                    {state.expirityDate === null ? (
                        'not yet choosen'
                    ) : (
                        <strong>{DateTime.from(state.expirityDate)?.utc().format('DD/MM/YYYY HH:mm')}</strong>
                    )}
                </div>
                <button
                    className='btn btn-primary'
                    type='submit'
                    data-test='set-play-break-button'
                    onClick={state.onSubmit}
                >
                    Set play break
                </button>
                <div
                    className='play-break-expiry'
                    data-test='play-break-expires'
                >
                    {state.playBreakOptions.playBreakExpiry === null ? null : (
                        <>
                            {' '}
                            Expires:{' '}
                            <strong>
                                {DateTime.from(state.playBreakOptions.playBreakExpiry)?.format('DD/MM/YYYY HH:mm')}
                            </strong>{' '}
                        </>
                    )}
                </div>
            </PageContainer>
        );
    }
);
